/**
 * Extracts a dominant brand colour from an image URL using <canvas>.
 *
 * Strategy: render the image into a 64×64 canvas, bucket each pixel into a
 * coarse 32-step RGB grid (so visually similar colours collapse together),
 * skip near-white, near-black and near-gray pixels (those produce muddy
 * accents), then return the hex of the heaviest bucket's average colour.
 *
 * Returns a hex string like "#FF6A00", or null if extraction fails (image
 * load error, all-gray cover, taint from cross-origin without CORS, etc.).
 */
export async function extractBrandColor(src) {
  if (!src) return null;
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onerror = () => resolve(null);
    img.onload = () => {
      try {
        const w = 64, h = 64;
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, w, h);
        const data = ctx.getImageData(0, 0, w, h).data;
        const buckets = new Map();
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
          if (a < 128) continue;
          const max = Math.max(r, g, b), min = Math.min(r, g, b);
          if (max < 30 || min > 230) continue;        // too dark / too bright
          if (max - min < 25) continue;                // greyish, no hue
          const key = `${r >> 5}_${g >> 5}_${b >> 5}`;
          const entry = buckets.get(key) || { r: 0, g: 0, b: 0, n: 0 };
          entry.r += r; entry.g += g; entry.b += b; entry.n++;
          buckets.set(key, entry);
        }
        if (buckets.size === 0) return resolve(null);
        let best = null;
        for (const e of buckets.values()) {
          if (!best || e.n > best.n) best = e;
        }
        const r = Math.round(best.r / best.n);
        const g = Math.round(best.g / best.n);
        const b = Math.round(best.b / best.n);
        const hex = "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("").toUpperCase();
        resolve(hex);
      } catch {
        resolve(null);
      }
    };
    img.src = src;
  });
}
